import React, { useState } from 'react';
import {
  IAuditoriaAdiantamentoViagemResultado,
  IItemAdiantamentoViagem,
} from '../interfaces/IAuditoriaAdiantamentoViagem';
import { formatCurrency, formatDate } from '../utils/formatters';

interface Props {
  resultado: IAuditoriaAdiantamentoViagemResultado;
}



// COR-298: drill-down a nível de lançamento — o ALERTA/AVISO precisa mostrar não só a data do
// último débito sem baixa, mas todos os lançamentos que compõem a reconstrução do saldo.
const LIMITE_LANCAMENTOS_VISIVEIS = 10;

const BlocoLancamentosRelacionados: React.FC<{ item: IItemAdiantamentoViagem }> = ({ item }) => {
  const [mostrarTodos, setMostrarTodos] = useState(false);
  const lancamentos = item.lancamentosRelacionados || [];

  if (lancamentos.length === 0) return null;

  const visiveis = mostrarTodos ? lancamentos : lancamentos.slice(0, LIMITE_LANCAMENTOS_VISIVEIS);

  return (
    <div className='mt-2'>
      <strong className='fs-8'>Lançamentos Relacionados ({lancamentos.length}):</strong>
      <div className='table-responsive mt-1'>
        <table className='table table-sm' style={{ fontSize: 12 }}>
          <thead>
            <tr>
              <th>Data</th>
              <th>Nº Lançamento</th>
              <th>Conta</th>
              <th>Histórico</th>
              <th className='text-end'>Débito</th>
              <th className='text-end'>Crédito</th>
            </tr>
          </thead>
          <tbody>
            {visiveis.map((lanc, idx) => (
              <tr key={`${lanc.numeroLancamento}-${lanc.data}-${idx}`}>
                <td>{formatDate(lanc.data)}</td>
                <td><code>{lanc.numeroLancamento || '-'}</code></td>
                <td><code>{lanc.contaContabil}</code></td>
                <td>{lanc.historico}</td>
                <td className='text-end'>{lanc.valorDebito > 0 ? formatCurrency(lanc.valorDebito) : '-'}</td>
                <td className='text-end'>{lanc.valorCredito > 0 ? formatCurrency(lanc.valorCredito) : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {lancamentos.length > LIMITE_LANCAMENTOS_VISIVEIS && !mostrarTodos && (
        <button
          type='button'
          className='btn btn-link btn-sm p-0 mt-1'
          style={{ fontSize: 12 }}
          onClick={() => setMostrarTodos(true)}
        >
          + {lancamentos.length - LIMITE_LANCAMENTOS_VISIVEIS} lançamento(s) adicional(is)
        </button>
      )}
    </div>
  );
};

const getIconeStatus = (status: string) => {
  switch (status) {
    case 'ERRO':
      return 'bi-exclamation-circle-fill';
    case 'ALERTA':
      return 'bi-exclamation-triangle-fill';
    case 'AVISO':
      return 'bi-info-circle-fill';
    default:
      return 'bi-check-circle-fill';
  }
};

const AuditoriaAdiantamentoViagemDetalhe: React.FC<Props> = ({ resultado }) => {
  const [itemExpandido, setItemExpandido] = useState<string | null>(null);
  const [fundamentacaoAberta, setFundamentacaoAberta] = useState(false);

  const adiantamentosPendentes = resultado.adiantamentosPendentes || [];
  const adiantamentosConciliados = resultado.adiantamentosConciliados || [];

  const alternarExpandir = (chave: string) => {
    setItemExpandido(itemExpandido === chave ? null : chave);
  };

  return (
    <>
      <div className={`auditoria-alert status-${resultado.status}`}>
        <i className={`bi ${getIconeStatus(resultado.status)} auditoria-alert-icon`} />
        <div>
          <strong>Auditoria de Adiantamentos de Viagens — {resultado.status}</strong>
          <div>{resultado.mensagemDirecionamento}</div>
        </div>
      </div>

      <div className='auditoria-kpis'>
        <div className={`auditoria-kpi ${resultado.impactoFiscalTotal > 0 ? 'auditoria-kpi-critico' : 'auditoria-kpi-disabled'}`}>
          <div className='auditoria-kpi-label'>Impacto Fiscal Estimado</div>
          <div className='auditoria-kpi-value'>{formatCurrency(resultado.impactoFiscalTotal)}</div>
          {resultado.impactoFiscalTotal === 0 ? (
            <span className='auditoria-kpi-selo'>Zero Desperdício</span>
          ) : (
            <div className='auditoria-kpi-help'>Multa ECD potencial — 5% sobre o saldo pendente, limitada a 1% da receita bruta do período (Art. 12, II, Lei nº 8.218/1991).</div>
          )}
        </div>

        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>Erros / Alertas / Avisos</div>
          <div className='auditoria-kpi-value'>
            <span className='auditoria-contador auditoria-contador-erro'>{resultado.totalErros}</span>{' '}
            <span className='auditoria-contador auditoria-contador-alerta'>{resultado.totalAlertas}</span>{' '}
            <span className='auditoria-contador auditoria-contador-aviso'>{resultado.totalAvisos}</span>
          </div>
          <div className='auditoria-kpi-help'>Adiantamentos de viagem pendentes por mais de 30 dias no período.</div>
        </div>

        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>Fundamentação</div>
          <div className='auditoria-kpi-value'>
            <button
              type='button'
              className='btn btn-sm btn-light-primary'
              onClick={() => setFundamentacaoAberta(!fundamentacaoAberta)}
            >
              <i className='bi bi-journal-check me-1' />
              {fundamentacaoAberta ? 'Ocultar fundamentação' : 'Ver fundamentação legal'}
            </button>
          </div>
          <div className='auditoria-kpi-help'>Base fiscal e contábil aplicada a esta auditoria.</div>
        </div>
      </div>

      {fundamentacaoAberta && (
        <div className='auditoria-card'>
          <div><strong>Fundamentação Fiscal:</strong> {resultado.fundamentacaoFiscal}</div>
          <div className='mt-2'><strong>Fundamentação Contábil:</strong> {resultado.fundamentacaoContabil}</div>
        </div>
      )}

      <div className='auditoria-card'>
        <h3>Adiantamentos de Viagens Pendentes (&gt; 30 dias sem prestação de contas)</h3>
        <p className='text-muted'>
          Contas de Adiantamento de Viagens (Ativo Circulante) com saldo devedor pendente de prestação de contas por
          mais de 30 dias — perda financeira e indício de despesa não reconhecida no período de competência devido.
        </p>

        {adiantamentosPendentes.length === 0 && (
          <div className='text-muted mt-4'>Nenhum adiantamento de viagem pendente identificado no período.</div>
        )}

        {adiantamentosPendentes.length > 0 && (
          <div className='table-responsive mt-4'>
            <table className='auditoria-table'>
              <thead>
                <tr>
                  <th>Conta</th>
                  <th>Data de Lançamento</th>
                  <th className='text-center'>Dias Pendentes</th>
                  <th className='text-end'>Valor Base</th>
                  <th className='text-end'>Multa ECD Potencial</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {adiantamentosPendentes.map((item: IItemAdiantamentoViagem, idx) => {
                  const chave = `av-${item.empresaId}-${item.contaContabil}-${idx}`;
                  const expandido = itemExpandido === chave;

                  return (
                    <React.Fragment key={chave}>
                      <tr className='auditoria-row-tributo status-alerta' onClick={() => alternarExpandir(chave)}>
                        <td>
                          <strong>{item.descricaoConta || item.contaContabil}</strong>
                          {item.contaReferencial && <div className='text-muted' style={{ fontSize: 11 }}>{item.contaReferencial}</div>}
                        </td>
                        <td>{formatDate(item.dataLancamento)}</td>
                        <td className='text-center'>
                          <span className={`auditoria-status status-${item.status}`}>{item.diasSemBaixa} dias</span>
                        </td>
                        <td className='text-end'>{formatCurrency(item.valorBase)}</td>
                        <td className='text-end'>{formatCurrency(item.impostoDevido)}</td>
                        <td>
                          <span className={`auditoria-status status-${item.status}`}>{item.status}</span>
                          <i className={`bi ${expandido ? 'bi-chevron-up' : 'bi-chevron-down'} ms-2 text-muted`} />
                        </td>
                      </tr>

                      {expandido && (
                        <tr>
                          <td colSpan={6} style={{ background: '#fffbeb' }}>
                            <div className='py-2 px-3'>
                              <div><strong>Alerta:</strong> Perda Financeira — Adiantamento de Viagem pendente há &gt; 30 dias (Ausência de Prestação de Contas).</div>
                              <div><strong>Lançamento (Histórico):</strong> {item.historico || 'não identificado'}.</div>
                              <div><strong>Conta Referencial:</strong> {item.contaReferencial || 'não mapeada'} - {item.descricaoConta}.</div>
                              <div><strong>Data de Lançamento:</strong> {formatDate(item.dataLancamento)}.</div>
                              <div><strong>Multa ECD Potencial:</strong> {formatCurrency(item.impostoDevido)}.</div>
                              <div><strong>Fundamentação Fiscal:</strong> {resultado.fundamentacaoFiscal}</div>
                              <div><strong>Fundamentação Contábil:</strong> {resultado.fundamentacaoContabil}</div>
                              <div className='mt-1 text-muted' style={{ fontSize: 12 }}>{item.memoriaCalculo}</div>
                              <BlocoLancamentosRelacionados item={item} />
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {adiantamentosConciliados.length > 0 && (
        <div className='auditoria-card'>
          <h3>
            <i className='bi bi-check-circle-fill text-success me-2' style={{ fontSize: 18 }} />
            Adiantamentos de Viagens Verificados e Conciliados
          </h3>
          <p className='text-muted'>
            Contas de adiantamento de viagem que foram auditadas e estão em conformidade — saldo zerado ou com
            prestação de contas dentro de 30 dias.
          </p>

          <div className='table-responsive mt-4'>
            <table className='auditoria-table'>
              <thead>
                <tr>
                  <th>Conta</th>
                  <th>Data de Lançamento</th>
                  <th className='text-center'>Dias</th>
                  <th className='text-end'>Saldo</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {adiantamentosConciliados.map((item: IItemAdiantamentoViagem, idx) => {
                  const chave = `avc-${item.empresaId}-${item.contaContabil}-${idx}`;
                  const expandido = itemExpandido === chave;

                  return (
                    <React.Fragment key={chave}>
                      <tr className='auditoria-row-tributo' onClick={() => alternarExpandir(chave)}>
                        <td>
                          <strong>{item.descricaoConta || item.contaContabil}</strong>
                          {item.contaReferencial && <div className='text-muted' style={{ fontSize: 11 }}>{item.contaReferencial}</div>}
                        </td>
                        <td>{item.dataLancamento ? formatDate(item.dataLancamento) : '-'}</td>
                        <td className='text-center'>{item.diasSemBaixa || '-'}</td>
                        <td className='text-end'>{formatCurrency(item.valorBase)}</td>
                        <td>
                          <span className={`auditoria-status status-${item.status}`}>{item.status}</span>
                          <i className={`bi ${expandido ? 'bi-chevron-up' : 'bi-chevron-down'} ms-2 text-muted`} />
                        </td>
                      </tr>

                      {expandido && (
                        <tr>
                          <td colSpan={5} style={{ background: '#f0fdf4' }}>
                            <div className='py-2 px-3'>
                              <i className='bi bi-check-circle-fill text-success me-1' />
                              <strong>Diagnóstico:</strong> {item.mensagemDiagnostico}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
};

export default AuditoriaAdiantamentoViagemDetalhe;
